import { storyblokEditable } from '@storyblok/react/rsc'
import { clsx } from 'clsx'
import { type FC } from 'react'

import { IconArrowRight } from '@redteclab/icons'

import { Link } from 'base-ui'

import { getStoryblokHrefLink } from '../../getStoryblokHrefLink'
import { type StoryblokComponentProps } from '../../model'
import { type StoryblokSectionType } from '../../model/storyblokTypes.generated'
import { StoryblokComponentsRenderer } from '../StoryblokComponentsRenderer'

export const StoryblokSection: FC<
  StoryblokComponentProps<StoryblokSectionType>
> = ({ blok }) => {
  const {
    _uid: uid,
    addRoundedCorners,
    addSpacing,
    anchorId,
    backgroundColor,
    clickToActionLink,
    clickToActionText,
    content,
    title,
    titleAlignment,
    type,
  } = blok

  const sectionId = anchorId ? anchorId.replace('#', '') : `section-${uid}`

  return (
    <section
      id={sectionId}
      {...storyblokEditable(blok)}
      className={clsx(
        'flex w-full items-center justify-center',
        backgroundColor === 'now' && 'bg-light-brand-low',
        backgroundColor === 'white' && 'bg-light-primary-low',
        backgroundColor === 'light' && 'bg-light-primary-medium',
        backgroundColor === 'brand-light' && 'bg-light-brand-medium',
        addSpacing && type === 'page' && 'px-4 py-8 tablet:py-16',
        addSpacing && type === 'modal' && 'px-6 py-4',
        addRoundedCorners && 'rounded-2xl',
      )}
    >
      <div className="flex w-full max-w-5xl flex-col gap-6 bg-inherit">
        {title ? (
          <div className="flex flex-col justify-between gap-2 tablet:flex-row">
            <h3
              className={clsx(
                type === 'page' && 'text-xl font-medium tablet:text-2xl',
                type === 'modal' && 'text-m font-medium',
                titleAlignment === 'right' && 'text-right',
                titleAlignment === 'center' && 'text-center',
                'flex-1',
              )}
            >
              {title}
            </h3>
            {clickToActionText ? (
              <Link
                className="mt-1.5 flex items-center text-s font-medium text-dark-brand no-underline hover:text-dark-primary-max"
                href={getStoryblokHrefLink(clickToActionLink)}
              >
                <span>{clickToActionText}</span>
                <IconArrowRight className="size-6" />
              </Link>
            ) : null}
          </div>
        ) : null}
        <div className="flex flex-wrap gap-6">
          <StoryblokComponentsRenderer bloks={content} />
        </div>
      </div>
    </section>
  )
}
